import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Search } from "lucide-react";
import { PRODUCT_CATEGORIES } from "../config/categories";
import { getAllRanges, searchRanges } from "../config/ranges";
import RangeCard from "../components/product/RangeCard";
import Footer from "../components/Footer";
import { useDocumentMeta } from "../hooks/useDocumentMeta";

/**
 * Products catalogue — browses PRODUCT RANGES / FAMILIES, not every SKU.
 */
export default function ProductsPage() {
  const [category, setCategory] = useState("all");
  const [query, setQuery] = useState("");

  useDocumentMeta({
    title: "Product Catalogue | DEBSUN Technologies",
    description:
      "Browse DEBSUN engineering product ranges — LED lighting, industrial solutions and digital platforms. Quote mode.",
  });

  const ranges = useMemo(() => {
    let list = query ? searchRanges(query) : getAllRanges();
    if (category !== "all") {
      list = list.filter((r) => r.category === category);
    }
    return list;
  }, [category, query]);

  return (
    <div className="min-h-screen pt-28" data-testid="products-page">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 pb-24">
        <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-[color:var(--gold)] mb-3">
          § Product Catalogue
        </div>
        <h1 className="display display-hero text-4xl md:text-6xl tracking-[-0.03em]">
          Engineering product ranges.
        </h1>
        <p className="mt-5 text-base md:text-lg text-steel-text leading-relaxed max-w-3xl">
          Browse by product family. Open a range to select the exact variant,
          review verified specifications and request a quote. Shop and Products
          share one Product Master — pricing is not published at this stage.
        </p>

        <div className="mt-10 flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2" data-testid="product-category-filters">
            <button
              onClick={() => setCategory("all")}
              className={`font-mono-2 text-[11px] tracking-[0.18em] uppercase px-4 py-2.5 border transition-colors ${
                category === "all"
                  ? "border-[color:var(--gold)] text-foreground"
                  : "border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              All
            </button>
            {PRODUCT_CATEGORIES.map((c) => (
              <button
                key={c.id}
                onClick={() => setCategory(c.id)}
                className={`font-mono-2 text-[11px] tracking-[0.18em] uppercase px-4 py-2.5 border transition-colors ${
                  category === c.id
                    ? "border-[color:var(--gold)] text-foreground"
                    : "border-border text-muted-foreground hover:text-foreground"
                }`}
                data-testid={`category-filter-${c.id}`}
              >
                {c.name}
              </button>
            ))}
          </div>

          <div className="relative w-full lg:w-80">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search range, wattage, SKU…"
              className="w-full bg-background border border-border pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[color:var(--gold)]"
              data-testid="product-search-input"
            />
          </div>
        </div>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
          {ranges.map((r, i) => (
            <RangeCard key={r.id} range={r} index={i} />
          ))}
        </div>

        {ranges.length === 0 && (
          <div className="mt-20 text-center text-muted-foreground">
            No product ranges match the current filters.
          </div>
        )}

        <div className="mt-16 border border-border bg-secondary/40 p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-2">
              Engineering Support
            </div>
            <p className="text-sm text-steel-text leading-relaxed max-w-xl">
              Need help selecting a product range or defining a custom
              configuration? Our engineering team can advise on application fit
              and project scope.
            </p>
          </div>
          <Link
            to="/#contact"
            className="btn-gold clip-frame-sm px-6 py-4 text-[11px] uppercase tracking-widest font-mono-2 inline-flex items-center gap-2"
          >
            Contact Engineering <ArrowUpRight size={14} />
          </Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}
